/**
 * VendorOutreachComposer — drop-in composer used both on a single Vendor
 * Detail page (per-vendor outreach) and inside the Query workspace
 * (query-driven outreach). It owns:
 *
 *   • A subject line (Email only)
 *   • A free-text message (auto-appends the operator's signature)
 *   • Three actions: WhatsApp · Email · Both
 *   • Posts every fire to /api/pollen/outreach so we get a journal +
 *     attach-vendor-to-query side-effect.
 *
 * The composer never sends anything itself — it opens wa.me / mailto in
 * the user's own client. The journal is the backend's job.
 */
import React, { useMemo, useState } from 'react';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, Megaphone, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useOperatorName, pollenSignature } from '@/hooks/useOperatorName';

const splitContacts = (s) => (s || '').split(/[,;]/).map(x => x.trim()).filter(Boolean);
const digitsOnly = (s) => (s || '').replace(/\D+/g, '');

export default function VendorOutreachComposer({
  vendor,
  queryId = null,
  defaultSubject = '',
  defaultBody = '',
  onSent,
}) {
  const { user } = useAuth();
  const operatorName = useOperatorName(user?.email, user?.name);
  const emails = useMemo(() => splitContacts(vendor?.email), [vendor]);
  const phones = useMemo(() => splitContacts(vendor?.phone), [vendor]);
  const [subject, setSubject] = useState(defaultSubject || `Pollen Dots × ${vendor?.name || ''}`);
  const [message, setMessage] = useState(defaultBody);
  const [sending, setSending] = useState(false);

  const signedBody = (raw) => `${raw.trim()}\n\n${pollenSignature(operatorName)}`;

  const log = async (channel, recipient) => {
    const t = localStorage.getItem('session_token');
    try {
      await fetch(`${API}/pollen/outreach`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify({
          vendor_id: vendor?.id,
          vendor_name: vendor?.name || '',
          vendor_type: vendor?.type || '',
          channel,
          subject: channel !== 'whatsapp' ? subject : '',
          message: signedBody(message),
          recipient,
          query_id: queryId || null,
        }),
      });
    } catch (e) { /* journal-only — don't block UX */ }
  };

  const fireEmail = async () => {
    if (!emails.length) { toast.error('No email on file for this vendor'); return; }
    if (!message.trim()) { toast.error('Type a message first'); return; }
    setSending(true);
    const cc = ['team@pollendots.com', ...emails.slice(1)].join(',');
    const url = `mailto:${encodeURIComponent(emails[0])}?cc=${encodeURIComponent(cc)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(signedBody(message))}`;
    window.location.href = url;
    await log('email', emails[0]);
    toast.success('Email draft opened — logged');
    setSending(false);
    onSent && onSent('email');
  };

  const fireWhatsApp = async () => {
    if (!phones.length) { toast.error('No phone on file for this vendor'); return; }
    if (!message.trim()) { toast.error('Type a message first'); return; }
    setSending(true);
    const digits = digitsOnly(phones[0]);
    const url = digits
      ? `https://wa.me/${digits}?text=${encodeURIComponent(signedBody(message))}`
      : `https://wa.me/?text=${encodeURIComponent(signedBody(message))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    await log('whatsapp', phones[0]);
    toast.success('WhatsApp opened — logged');
    setSending(false);
    onSent && onSent('whatsapp');
  };

  const fireBoth = async () => {
    if (!emails.length && !phones.length) { toast.error('No email or phone on file'); return; }
    if (!message.trim()) { toast.error('Type a message first'); return; }
    setSending(true);
    // Open WhatsApp first (new tab) then mailto (current tab). Log both.
    if (phones.length) {
      const digits = digitsOnly(phones[0]);
      const wa = digits ? `https://wa.me/${digits}?text=${encodeURIComponent(signedBody(message))}` : null;
      if (wa) window.open(wa, '_blank', 'noopener,noreferrer');
      await log('whatsapp', phones[0]);
    }
    if (emails.length) {
      const cc = ['team@pollendots.com', ...emails.slice(1)].join(',');
      const mailto = `mailto:${encodeURIComponent(emails[0])}?cc=${encodeURIComponent(cc)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(signedBody(message))}`;
      window.location.href = mailto;
      await log('email', emails[0]);
    }
    toast.success('Both channels opened — logged');
    setSending(false);
    onSent && onSent('both');
  };

  return (
    <div className="rounded-lg border bg-white p-3 space-y-3" data-testid="vendor-outreach-composer">
      <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Send to {vendor?.name || 'vendor'}</span>
        {emails.length > 0 && <span title={emails.join(', ')}>· {emails.length} email{emails.length > 1 ? 's' : ''}</span>}
        {phones.length > 0 && <span title={phones.join(', ')}>· {phones.length} phone{phones.length > 1 ? 's' : ''}</span>}
        {queryId && <span className="text-amber-700">· logged on query</span>}
      </div>
      <div>
        <Label className="text-xs">Subject <span className="text-muted-foreground">(email only)</span></Label>
        <Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="outreach-subject" />
      </div>
      <div>
        <Label className="text-xs">Message — your signature is added automatically</Label>
        <Textarea
          rows={6}
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder={`E.g. "We need a quote for a Sumo from Kochi airport to Munnar (drop on 18 Aug, pickup on 22 Aug, 4 pax + luggage). Please share rate, vehicle photo, and driver name."`}
          data-testid="outreach-body"
        />
        <p className="text-[10px] text-muted-foreground mt-1 whitespace-pre-line">
          Signed as: <strong>{operatorName || user?.name || 'Operator'}</strong> · Team Pollen Dots
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          className="border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
          onClick={fireWhatsApp}
          disabled={sending || !phones.length}
          data-testid="outreach-send-whatsapp"
        >
          <Megaphone className="w-4 h-4 mr-1" />WhatsApp
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100"
          onClick={fireEmail}
          disabled={sending || !emails.length}
          data-testid="outreach-send-email"
        >
          <Mail className="w-4 h-4 mr-1" />Email
        </Button>
        <Button
          size="sm"
          onClick={fireBoth}
          disabled={sending || (!emails.length && !phones.length)}
          data-testid="outreach-send-both"
        >
          {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
          Both
        </Button>
      </div>
    </div>
  );
}
